import React, { ReactElement, useEffect, useState } from 'react';
import {
  MenuIcon,
  DocumentsIcon,
  HelpOutlineIcon,
  PlayCircleOutlineIcon,
  PauseCircleOutlineIcon,
  ArrowBackIosIcon,
  ArrowForwardIosIcon,
  Button,
  SignOutIcon,
} from 'elmo-elements';
import { ThemeBottomType } from '@/modules/types/theme';
import { actions as AudioPlayerActions } from '@/components/Player/modules/reducers/audioPlayer';
import { actions as ElearningPlayerActions } from '@/components/Player/modules/reducers/elearningPlayer';
import { useDispatch, useSelector } from 'react-redux';
import { AudioPlayerDataType } from '@/modules/types/audioPlayer';
import ProgressBar from '@/components/Player/ProgressBar/progressBar';
import { updatePlayPauseIcon } from '../FrameContent/util';
import { MuteType } from '@/modules/types/elearning/muteType.enum';

interface FrameBottomViewPropsType {
  bottom: ThemeBottomType;
  // eslint-disable-next-line react/require-default-props
  isPlayable?: boolean;
  isMobileView?: boolean;
}

const FrameBottomView = ({
  bottom,
  isPlayable,
  isMobileView,
}: FrameBottomViewPropsType): ReactElement => {
  const dispatch = useDispatch();
  const [hasAutoplayedFrameId, setHasAutoplayedFrameId] = useState<string>('');

  const audioPlayer: AudioPlayerDataType = useSelector(
    (state: { audioPlayer: AudioPlayerDataType }) => state.audioPlayer,
  );

  const renderMenu = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.playerMetadata.showMenu
    ) {
      const menuClickHandler = () => {
        if (isPlayable)
          dispatch({
            type: ElearningPlayerActions.toggleMenu.type,
          });
      };
      return (
        <li id="index-btn" className="control-btn">
          <Button
            id="FrameBottomMenuBtn"
            ariaLabel="Menu"
            tooltip={{ title: 'Menu', placement: 'top' }}
            icon={<MenuIcon />}
            onClick={menuClickHandler}
          />
        </li>
      );
    }
    return <div />;
  };

  const renderResources = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.playerMetadata.showResource
    ) {
      const resourcesClickHandler = () => {
        if (isPlayable)
          dispatch({
            type: ElearningPlayerActions.toggleResources.type,
          });
      };
      return (
        <li id="resource-btn" className="control-btn">
          <Button
            id="FrameBottomResourceBtn"
            ariaLabel="Resources"
            tooltip={{ title: 'Resources', placement: 'top' }}
            icon={<DocumentsIcon />}
            onClick={resourcesClickHandler}
          />
        </li>
      );
    }
    return <div />;
  };

  const renderHelp = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.showHelpDesk
    ) {
      const helperClickHandler = () => {
        if (isPlayable)
          dispatch({
            type: ElearningPlayerActions.toggleHelper.type,
          });
      };
      return (
        <li id="help-btn" className="control-btn">
          <Button
            id="FrameBottomHelpBtn"
            ariaLabel="Help"
            tooltip={{ title: 'Help', placement: 'top' }}
            icon={<HelpOutlineIcon />}
            onClick={helperClickHandler}
          />
        </li>
      );
    }
    return <div />;
  };

  const renderQuizHelp = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.showHelpDesk
    ) {
      const quizHelperClickHandler = () => {
        dispatch({
          type: ElearningPlayerActions.toggleQuizHelper.type,
        });
      };
      return (
        <li id="help-btn" className="control-btn">
          <Button
            id="FrameBottomQuizHelpBtn"
            ariaLabel="Help"
            tooltip={{ title: 'Help', placement: 'top' }}
            icon={<HelpOutlineIcon />}
            onClick={quizHelperClickHandler}
          />
        </li>
      );
    }
    return <div />;
  };

  const renderPowerBY = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.showPoweredBy
    ) {
      return (
        <li id="power-by-text">
          <p className="navbar-text">
            <a
              href="https://elmosoftware.com.au"
              rel="noopener noreferrer"
              target="_blank"
              className="navbar-link"
            >
              Powered by ELMO
            </a>
          </p>
        </li>
      );
    }
    return <div />;
  };

  const handleSoundPlayPause = (playStatus?: boolean | null) => {
    const source = bottom.metadata.sound;
    if (source) {
      switch (true) {
        case playStatus:
          dispatch({
            type: AudioPlayerActions.playAudio.type,
            payload: { source },
          });
          break;
        case playStatus === false:
          dispatch({
            type: AudioPlayerActions.stopAudio.type,
            payload: { source },
          });
          break;
        case playStatus === null:
          dispatch({
            type: AudioPlayerActions.endAudio.type,
          });
          break;

        default:
          dispatch({
            type: AudioPlayerActions.playPauseAudio.type,
            payload: { source },
          });
          break;
      }
    }
  };

  /* Keyboard functionalities */
  useEffect(() => {
    const keydownListener = (keyboardEvent: KeyboardEvent) => {
      // prevent default here will stop play/pause keys on embedded video - need it as there is a conflict in behavior
      keyboardEvent.preventDefault();
      switch (keyboardEvent.key) {
        case 'ArrowRight':
          if (
            bottom.metadata.navigation.currentFramePage !==
            bottom.metadata.navigation.total
          ) {
            bottom.metadata.navigation.goNextFrame();
            handleSoundPlayPause(null);
          }
          break;
        case 'ArrowLeft':
          if (bottom.metadata.navigation.currentFramePage !== 1) {
            bottom.metadata.navigation.goPrevFrame();
            handleSoundPlayPause(null);
          }
          break;
        case 'm':
          dispatch({
            type: AudioPlayerActions.setMute.type,
            payload: !audioPlayer.mute,
          });
          break;
        case ' ':
          updatePlayPauseIcon(undefined);
          handleSoundPlayPause();
          break;

        default:
          break;
      }
    };

    window.addEventListener('keyup', keydownListener, true);
    return () => window.removeEventListener('keyup', keydownListener, true);
  }, [bottom, audioPlayer]);

  useEffect(() => {
    if (
      bottom &&
      bottom.metadata.sound &&
      bottom.metadata.playerMetadata &&
      bottom.metadata.currentFrame.frameMetadata.mute === 2 &&
      // This to allow autoplay for changing frames & prevent loop playbacks
      hasAutoplayedFrameId !== `${bottom.metadata.currentFrame.id}`
    ) {
      handleSoundPlayPause(true); // autoplay
    }

    if (bottom.metadata.currentFrame?.id)
      setHasAutoplayedFrameId(`${bottom.metadata.currentFrame.id}`);
  }, [audioPlayer.loaded, audioPlayer.source, audioPlayer.howler, bottom]);

  const renderSoundControl = () => {
    if (
      Object.keys(bottom.metadata).length > 0 &&
      bottom.metadata.mute !== MuteType.HIDDEN
    ) {
      return (
        <ul id="sound-control" className="nav navbar-nav navbar-center">
          <li id="sound-control-btn" className="control-btn">
            <Button
              id="FrameBottomPauseBtn"
              key={`${audioPlayer.playing}`}
              ariaLabel="Toggle audio"
              tooltip={{ title: 'Toggle audio', placement: 'top' }}
              icon={
                audioPlayer.playing &&
                audioPlayer.source === bottom.metadata.sound ? (
                  <PauseCircleOutlineIcon />
                ) : (
                  <PlayCircleOutlineIcon />
                )
              }
              className="sound-control"
              onClick={() => handleSoundPlayPause()}
            />
          </li>
        </ul>
      );
    }
    return <div />;
  };

  const setFrameStyle = () => {
    if (bottom && Object.keys(bottom.metadata).length > 0) {
      const FrameBottomStyleID = 'FrameBottomStyle';
      const FrameBottomStyle = document.getElementById(FrameBottomStyleID);
      if (FrameBottomStyle) {
        FrameBottomStyle.remove();
      }
      const style = document.createElement('style');
      style.setAttribute('id', FrameBottomStyleID);
      document.head.appendChild(style);
      const stylesheet = style.sheet;
      const stylePropertyPrefix = '.module-viewer-style ';
      // TO-DO: image path need updated once theme api is ready
      const menuIconSet = bottom.metadata.menu.menuIconSet ? 'dark' : 'white';
      (stylesheet as CSSStyleSheet).insertRule(
        `${stylePropertyPrefix} .navbar-nav li { height: 70px;
        width: 70px}`,
        0,
      );
      if (bottom.metadata.menu.isGradientMenu) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #navigation { background-image: linear-gradient(to bottom, #${bottom.metadata.menu.menuColor1} 0%, #${bottom.metadata.menu.menuColor2} 100%)}`,
          0,
        );
      } else {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #navigation { background-color: #${bottom.metadata.menu.menuColor1}}`,
          0,
        );
      }
      (stylesheet as CSSStyleSheet).insertRule(
        `${stylePropertyPrefix} #navigation li a { text-indent: -9999px; cursor: default}`,
        0,
      );
      (stylesheet as CSSStyleSheet).insertRule(
        `${stylePropertyPrefix} .navbar-nav .navbar-text, ${stylePropertyPrefix} #navigation .navbar-nav li.control-btn button { color: ${
          menuIconSet === 'white' ? 'white' : '#4d4d4d'
        }}`,
        0,
      );
      (stylesheet as CSSStyleSheet).insertRule(
        `${stylePropertyPrefix} .navbar-default .navbar-link { color: white}`,
        0,
      );
    }
  };

  useEffect(() => {
    setFrameStyle();
  }, [bottom]);
  return (
    <>
      <div className="module-viewer-style">
        <div id="progress-bar" className="canvas-progress">
          {!bottom.metadata.isQuiz && <ProgressBar />}
        </div>
      </div>
      <div className="module-viewer-style">
        <div id="navigation" className="canvas-bottom">
          <nav
            id="navigation"
            className="navbar navbar-default navbar-fixed-bottom body-font-items"
            role="navigation"
            style={{ display: isMobileView ? 'flex' : 'block' }}
          >
            <ul className="nav navbar-nav navbar-left">
              {!bottom.metadata.isQuiz && renderMenu()}
              {!bottom.metadata.isQuiz && renderResources()}
              {!bottom.metadata.isQuiz && renderHelp()}
              {!bottom.metadata.isQuiz && renderPowerBY()}
              {bottom.metadata.isQuiz && renderQuizHelp()}
            </ul>
            {bottom.metadata.sound && renderSoundControl()}

            {!bottom.metadata.isQuiz && (
              <ul id="page-navigation" className="nav navbar-nav navbar-right">
                {bottom.metadata.navigation.currentFramePage !== 1 && (
                  <li id="back-btn" className="control-btn">
                    <Button
                      id="FrameBottomPrevBtn"
                      onClick={() => {
                        bottom.metadata.navigation.goPrevFrame();
                        handleSoundPlayPause(null);
                      }}
                      ariaLabel="Previous"
                      tooltip={{ title: 'Previous', placement: 'top' }}
                      icon={<ArrowBackIosIcon />}
                    />
                  </li>
                )}

                <li id="page-info">
                  {bottom.metadata.navigation.currentFramePage === 1 ? (
                    <p className="navbar-text" id="begin-text">
                      Click next to begin...
                    </p>
                  ) : (
                    <p className="navbar-text" id="page-text">
                      <span id="current-page">
                        {bottom.metadata.navigation.currentFramePage
                          ? bottom.metadata.navigation.currentFramePage - 1
                          : bottom.metadata.navigation.currentFramePage}
                      </span>{' '}
                      of{' '}
                      {bottom.metadata.navigation.total
                        ? bottom.metadata.navigation.total - 1
                        : bottom.metadata.navigation.total}
                    </p>
                  )}
                </li>

                <li id="next-btn" className="control-btn">
                  {bottom.metadata.navigation.currentFramePage !==
                    bottom.metadata.navigation.total && (
                    <Button
                      id="FrameBottomNextBtn"
                      ariaLabel="Next"
                      tooltip={{ title: 'Next', placement: 'top' }}
                      onClick={() => {
                        bottom.metadata.navigation.goNextFrame();
                        handleSoundPlayPause(null);
                      }}
                      icon={<ArrowForwardIosIcon />}
                    />
                  )}
                </li>
              </ul>
            )}
            {bottom.metadata.isQuiz && (
              <ul id="cancel-btn" className="nav navbar-nav navbar-right">
                <li className="control-btn">
                  <Button
                    ariaLabel="Cancel"
                    tooltip={{ title: 'Cancel', placement: 'top' }}
                    onClick={bottom.metadata.navigation.exitQuiz}
                    icon={<SignOutIcon />}
                  />
                </li>
              </ul>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default FrameBottomView;
